// src/pages/admin/ManualBilling.jsx
import React, { useState } from "react";
import { adminService } from "../../services/admin.service";
import { generateInvoice } from "../../utils/generateInvoice"; // Standard Invoice
import { generateB2BInvoice } from "../../utils/generateB2BInvoice"; // B2B Invoice
import "./ManualBilling.css";

export default function ManualBilling() {
  const [loading, setLoading] = useState(false);

  // ✅ NEW: State for the toggle button
  const [invoiceType, setInvoiceType] = useState("B2C"); // Defaults to Standard

  const [customer, setCustomer] = useState({
    name: "",
    company_name: "",
    gstin: "",
    phone: "",
    address: "",
    state: "West Bengal",
    pincode: "",
  });

  const [items, setItems] = useState([
    { name: "", sku: "", hsn: "", qty: 1, unit_price: 0, gst_rate: 18 },
  ]);

  const [extras, setExtras] = useState({
    shipping_fee: 0,
    discount_amount: 0,
  });

  const handleCustomerChange = (e) => {
    setCustomer({ ...customer, [e.target.name]: e.target.value });
  };

  const handleExtrasChange = (e) => {
    setExtras({ ...extras, [e.target.name]: Number(e.target.value) });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItemRow = () => {
    setItems([
      ...items,
      { name: "", sku: "", hsn: "", qty: 1, unit_price: 0, gst_rate: 18 },
    ]);
  };

  const removeItemRow = (index) => {
    if (items.length === 1) return;
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const calculateTotals = () => {
    let taxable = 0;
    let gst = 0;

    items.forEach((item) => {
      const itemTaxable = item.qty * item.unit_price;
      const itemGst = itemTaxable * (item.gst_rate / 100);
      taxable += itemTaxable;
      gst += itemGst;
    });

    if (extras.shipping_fee > 0) {
      const shipTaxable = extras.shipping_fee / 1.18;
      const shipGst = extras.shipping_fee - shipTaxable;
      taxable += shipTaxable;
      gst += shipGst;
    }

    const grand = taxable + gst - extras.discount_amount;
    return { taxable, gst, grand };
  };

  const totals = calculateTotals();

  const handleGenerateBill = async () => {
    // Basic validation
    if (!customer.name || items.some((i) => !i.name || i.unit_price <= 0)) {
      return alert(
        "Please fill in the customer name and ensure all items have a valid price.",
      );
    }

    // B2B Validation: Ensure GSTIN is there if B2B is selected
    if (invoiceType === "B2B" && !customer.gstin) {
      return alert("Please provide the Customer GSTIN for a B2B invoice.");
    }

    try {
      setLoading(true);

      const finalAddress = customer.pincode
        ? `${customer.address}, PIN: ${customer.pincode}`
        : customer.address;

      const payload = {
        invoice_type: invoiceType, // ✅ Pass explicit type to backend
        customer: {
          ...customer,
          address: finalAddress,
        },
        items,
        shipping_fee: extras.shipping_fee,
        discount_amount: extras.discount_amount,
      };

      const response = await adminService.createManualInvoice(payload);

      // ✅ Trigger the correct PDF generator based on the toggle state
      if (invoiceType === "B2B") {
        generateB2BInvoice(response.invoice);
      } else {
        generateInvoice(response.invoice);
      }

      // Reset form
      setCustomer({
        name: "",
        company_name: "",
        gstin: "",
        phone: "",
        address: "",
        state: "West Bengal",
        pincode: "",
      });
      setItems([
        { name: "", sku: "", hsn: "", qty: 1, unit_price: 0, gst_rate: 18 },
      ]);
      setExtras({ shipping_fee: 0, discount_amount: 0 });

      alert(`Success! Invoice ${response.invoice.invoice_number} generated.`);
    } catch (error) {
      console.error(error);
      alert("Failed to generate manual invoice.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-wrapper">
      <div className="mb-header-section">
        <h1 className="mb-page-title">Manual Bill Generator</h1>
      </div>

      {/* ✅ NEW TOGGLE BUTTONS */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <button
          onClick={() => setInvoiceType("B2C")}
          style={{
            padding: "10px 20px",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
            backgroundColor: invoiceType === "B2C" ? "#2563eb" : "#e2e8f0",
            color: invoiceType === "B2C" ? "#ffffff" : "#475569",
            transition: "all 0.2s",
          }}
        >
          Standard Bill (B2C)
        </button>
        <button
          onClick={() => setInvoiceType("B2B")}
          style={{
            padding: "10px 20px",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
            backgroundColor: invoiceType === "B2B" ? "#2563eb" : "#e2e8f0",
            color: invoiceType === "B2B" ? "#ffffff" : "#475569",
            transition: "all 0.2s",
          }}
        >
          Business Bill (B2B)
        </button>
      </div>

      <div className="mb-grid">
        <div className="mb-form-column">
          <div className="mb-card">
            <h2 className="mb-card-title">Customer Details</h2>
            <div className="mb-input-grid">
              <div className="mb-input-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  name="name"
                  value={customer.name}
                  onChange={handleCustomerChange}
                  placeholder="John Doe"
                />
              </div>
              <div className="mb-input-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  name="phone"
                  value={customer.phone}
                  onChange={handleCustomerChange}
                  placeholder="9876543210"
                />
              </div>

              {/* ✅ CONDITIONALLY RENDER B2B FIELDS based on toggle */}
              {invoiceType === "B2B" && (
                <>
                  <div
                    className="mb-input-group"
                    style={{ animation: "fadeIn 0.3s ease-in-out" }}
                  >
                    <label>Company Name (B2B)</label>
                    <input
                      type="text"
                      name="company_name"
                      value={customer.company_name}
                      onChange={handleCustomerChange}
                      placeholder="Client Pvt Ltd"
                    />
                  </div>
                  <div
                    className="mb-input-group"
                    style={{ animation: "fadeIn 0.3s ease-in-out" }}
                  >
                    <label>Customer GSTIN *</label>
                    <input
                      type="text"
                      name="gstin"
                      value={customer.gstin}
                      onChange={handleCustomerChange}
                      placeholder="27AADCB2230M1Z2"
                      maxLength="15"
                      style={{ textTransform: "uppercase" }}
                    />
                  </div>
                </>
              )}

              <div className="mb-input-group full-width">
                <label>Billing Address</label>
                <input
                  type="text"
                  name="address"
                  value={customer.address}
                  onChange={handleCustomerChange}
                  placeholder="123 Street Name, City"
                />
              </div>
              <div className="mb-input-group">
                <label>Pincode</label>
                <input
                  type="text"
                  name="pincode"
                  value={customer.pincode}
                  onChange={handleCustomerChange}
                  placeholder="700091"
                />
              </div>
              <div className="mb-input-group">
                <label>Place of Supply (State)</label>
                <input
                  type="text"
                  name="state"
                  value={customer.state}
                  onChange={handleCustomerChange}
                  placeholder="West Bengal"
                />
              </div>
            </div>
          </div>

          <div className="mb-card">
            <div className="mb-card-header">
              <h2 className="mb-card-title">Product Items</h2>
              <button onClick={addItemRow} className="mb-btn-add">
                + Add Item
              </button>
            </div>
            <div className="mb-table-container">
              <table className="mb-table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>SKU</th>
                    <th>HSN</th>
                    <th>Qty</th>
                    <th>Base Price (Excl)</th>
                    <th>GST %</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) =>
                            handleItemChange(index, "name", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={item.sku}
                          onChange={(e) =>
                            handleItemChange(index, "sku", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={item.hsn}
                          onChange={(e) =>
                            handleItemChange(index, "hsn", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          value={item.qty}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "qty",
                              Number(e.target.value),
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          value={item.unit_price}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "unit_price",
                              Number(e.target.value),
                            )
                          }
                        />
                      </td>
                      <td>
                        <select
                          value={item.gst_rate}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "gst_rate",
                              Number(e.target.value),
                            )
                          }
                        >
                          <option value="0">0%</option>
                          <option value="5">5%</option>
                          <option value="12">12%</option>
                          <option value="18">18%</option>
                          <option value="28">28%</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <button
                          onClick={() => removeItemRow(index)}
                          className="mb-btn-remove"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-card">
            <h2 className="mb-card-title">Additional Charges & Discounts</h2>
            <div className="mb-input-grid">
              <div className="mb-input-group">
                <label>Shipping / Handling Fee (₹)</label>
                <input
                  type="number"
                  min="0"
                  name="shipping_fee"
                  value={extras.shipping_fee}
                  onChange={handleExtrasChange}
                />
              </div>
              <div className="mb-input-group">
                <label>Discount Amount (₹)</label>
                <input
                  type="number"
                  min="0"
                  name="discount_amount"
                  value={extras.discount_amount}
                  onChange={handleExtrasChange}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-summary-column">
          <div className="mb-summary-card">
            <h2 className="mb-summary-title">Bill Summary</h2>
            <div className="mb-summary-rows">
              <div className="mb-summary-row">
                <span>Total Taxable Value</span>
                <span>₹ {totals.taxable.toFixed(2)}</span>
              </div>
              <div className="mb-summary-row">
                <span>Total CGST/IGST</span>
                <span>₹ {totals.gst.toFixed(2)}</span>
              </div>
              {extras.discount_amount > 0 && (
                <div className="mb-summary-row discount">
                  <span>Discount</span>
                  <span>- ₹ {extras.discount_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="mb-summary-grand">
                <span>Grand Total</span>
                <span>₹ {Math.round(totals.grand).toFixed(2)}</span>
              </div>
            </div>
            <button
              onClick={handleGenerateBill}
              disabled={loading}
              className="mb-btn-generate"
            >
              {loading ? "Processing..." : `Generate ${invoiceType} Bill`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
